import { useState, useEffect, useCallback } from "react";

// The IT Number is rendered in the Label Info section and in the Ref Numbers section and has to
// read the same in both. The in-flight value is held module wide so an edit in one section shows
// up in the other straight away, and it is scoped to a single order so it can never carry over
// to whichever order is opened next.
//
// `undefined` means "nobody has edited it in this scope yet", which lets a consumer fall back to
// the value it loaded from the server. An empty string is a deliberate clear and is preserved.
let sharedScope;
let sharedValue;
const listeners = new Set();

function broadcast(newValue) {
  sharedValue = newValue;
  listeners.forEach((listener) => listener(newValue));
}

export default function useItNumberSync(scope) {
  const [value, setValue] = useState(() => (sharedScope === scope ? sharedValue : undefined));

  useEffect(() => {
    listeners.add(setValue);
    if (sharedScope === scope) {
      setValue(sharedValue);
    } else {
      sharedScope = scope;
      broadcast(undefined);
    }

    return () => {
      listeners.delete(setValue);
      if (listeners.size === 0) {
        sharedScope = undefined;
        sharedValue = undefined;
      }
    };
  }, [scope]);

  const updateValue = useCallback((newValue) => {
    sharedScope = scope;
    broadcast(newValue);
  }, [scope]);

  return [value, updateValue];
}
